"use client";

import React from "react";
import { Dropdown, ConfigProvider } from "antd";
import type { DropdownProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import {
  MoreOutlined,
  InboxOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "../styles/action-menu.css";

export interface ActionMenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  actionType?: "archive" | "delete" | "cancel";
  onClick?: () => void;
  disabled?: boolean;
}

export interface ActionMenuProps extends Omit<DropdownProps, "menu"> {
  /**
   * Menu items to display
   */
  items?: ActionMenuItem[];

  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Default menu items (archive, cancel, delete)
   */
  showDefaultItems?: boolean;

  /**
   * Callback when archive is clicked
   */
  onArchive?: () => void;

  /**
   * Callback when delete is clicked
   */
  onDelete?: () => void;

  /**
   * Callback when cancel is clicked
   */
  onCancel?: () => void;
}

/**
 * ActionMenu component - Dropdown menu with action items
 *
 * This component provides an action menu based on Ant Design Dropdown
 * with OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (specific colors, hover states)
 * - Default action items (archive, cancel, delete)
 * - Custom menu items support
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Dropdown features and props
 *
 * @example
 * ```tsx
 * // Basic action menu with default items
 * <ActionMenu
 *   onArchive={handleArchive}
 *   onDelete={handleDelete}
 *   onCancel={handleCancel}
 * />
 *
 * // With custom items
 * <ActionMenu
 *   items={[
 *     { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: handleEdit },
 *     { key: 'duplicate', label: 'Duplicate', icon: <CopyOutlined />, onClick: handleDuplicate },
 *   ]}
 * />
 *
 * // With custom trigger
 * <ActionMenu
 *   trigger={['click']}
 *   onArchive={handleArchive}
 * >
 *   <Button>Custom Trigger</Button>
 * </ActionMenu>
 * ```
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  className,
  showDefaultItems = true,
  onArchive,
  onDelete,
  onCancel,
  children,
  ...props
}) => {
  const defaultItems: ActionMenuItem[] = [
    {
      key: "archive",
      label: "Archive",
      icon: <InboxOutlined />,
      actionType: "archive",
      onClick: onArchive,
    },
    {
      key: "cancel",
      label: "Cancel",
      icon: <CloseOutlined />,
      actionType: "cancel",
      onClick: onCancel,
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      actionType: "delete",
      onClick: onDelete,
    },
  ];

  const menuItems = items || (showDefaultItems ? defaultItems : []);

  const menu = {
    items: menuItems.map((item) => ({
      key: item.key,
      label: (
        <div className="orion-action-menu-item">
          {item.icon}
          <span className="orion-action-menu-item-text">{item.label}</span>
        </div>
      ),
      onClick: item.onClick,
      disabled: item.disabled,
    })),
    style: {
      borderRadius: "4px",
      padding: "8px",
      boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      border: "1px solid #D0D5DD",
      minWidth: 120,
    },
  };

  return (
    <ConfigProvider theme={orionTheme}>
      <Dropdown
        menu={menu}
        trigger={["click"]}
        className={clsx("orion-action-menu", className)}
        {...props}
      >
        {children || (
          <button className="orion-action-menu-trigger">
            <MoreOutlined />
          </button>
        )}
      </Dropdown>
    </ConfigProvider>
  );
};

ActionMenu.displayName = "ActionMenu";
